'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { Slide, type FeaturedSlide } from './Slide';
import { useSplashHold } from '../../contexts/SplashContext';

export type { FeaturedSlide };

interface Props {
  slides: FeaturedSlide[];
  onSlideClick?: (href: string) => void;
}

// react-slick mutates the DOM after mount and produces output that the
// preload scanner can't use to discover the LCP image during streaming.
// We split the carousel off into a dynamic chunk (no SSR) and render the
// first slide statically so the LCP image lives in the initial HTML.
// Once the slider mounts client-side it covers the static slide and
// takes over.
const FeaturedEventsBannerSlider = dynamic(
  () => import('./FeaturedEventsBannerSlider'),
  { ssr: false, loading: () => null },
);

const FeaturedEventsBanner: React.FC<Props> = ({ slides, onSlideClick }) => {
  const [sliderReady, setSliderReady] = useState(false);
  // The slider takes over from the full-width static slide with a different
  // layout (peeking neighbours, arrows), so keep the first-load splash up until then.
  useSplashHold(sliderReady || slides.length < 2);

  if (slides.length === 0) {
    return null;
  }

  if (slides.length === 1) {
    return (
      <div className="relative w-full rounded-3xl overflow-hidden">
        <Slide slide={slides[0]} onClick={onSlideClick} priority />
      </div>
    );
  }

  // Static first slide is always rendered for the SSR pass — it carries
  // `priority` so next/image emits a `<link rel="preload">` for the
  // banner image in the initial HTML. Once the dynamic slick chunk
  // mounts, the slider sits on top (visually identical first frame) and
  // we collapse the static placeholder out of flow so the layout doesn't
  // double up.
  return (
    <div className="relative w-full">
      <div
        className={sliderReady ? 'invisible pointer-events-none absolute inset-0 -z-10' : 'relative'}
        aria-hidden={sliderReady}
      >
        <Slide slide={slides[0]} onClick={onSlideClick} priority />
      </div>
      <FeaturedEventsBannerSlider
        slides={slides}
        onSlideClick={onSlideClick}
        onReady={() => setSliderReady(true)}
      />
    </div>
  );
};

export default FeaturedEventsBanner;
